import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { ReplaceAroundStep, ReplaceStep } from '@atlaskit/editor-prosemirror/transform';

import {
	REDACTION_EXTENSION_KEY,
	REDACTION_EXTENSION_TYPE,
	REDACTION_MATCHER_SOURCES,
} from '../types';

const INLINE_EXTENSION_NODE_TYPE = 'inlineExtension';

export type RedactionExtensionAttrs = {
	extensionKey?: unknown;
	extensionType?: unknown;
};

/** An absent key is accepted (legacy nodes); a different key is not. */
export const isRedactionExtensionAttrs = (
	attrs: RedactionExtensionAttrs | null | undefined,
): boolean =>
	attrs?.extensionType === REDACTION_EXTENSION_TYPE &&
	(attrs.extensionKey === undefined ||
		attrs.extensionKey === null ||
		attrs.extensionKey === REDACTION_EXTENSION_KEY);

type AdfNodeLike = {
	attrs?: RedactionExtensionAttrs;
	content?: unknown;
	type?: unknown;
};

export const isRedactionAdfNode = (node: unknown): boolean => {
	if (!node || typeof node !== 'object') {
		return false;
	}
	const { type, attrs } = node as AdfNodeLike;
	return type === INLINE_EXTENSION_NODE_TYPE && isRedactionExtensionAttrs(attrs);
};

export const countRedactionNodesInAdf = (adf: unknown): number => {
	if (!adf || typeof adf !== 'object') {
		return 0;
	}
	const node = adf as AdfNodeLike;
	let count = isRedactionAdfNode(node) ? 1 : 0;
	if (Array.isArray(node.content)) {
		for (const child of node.content) {
			count += countRedactionNodesInAdf(child);
		}
	}
	return count;
};

export const isRedactionNode = (node: PMNode): boolean =>
	node.type.name === INLINE_EXTENSION_NODE_TYPE && isRedactionExtensionAttrs(node.attrs);

export const countRedactionNodesInDoc = (doc: PMNode): number => {
	let count = 0;
	doc.descendants((node) => {
		if (isRedactionNode(node)) {
			count++;
		}
	});
	return count;
};

const countRedactionsInFragment = (fragment: Fragment): number => {
	let count = 0;
	fragment.descendants((node) => {
		if (isRedactionNode(node)) {
			count++;
		}
	});
	return count;
};

const countRedactionsBetween = (doc: PMNode, from: number, to: number): number => {
	let count = 0;
	if (to > from) {
		doc.nodesBetween(from, to, (node) => {
			if (isRedactionNode(node)) {
				count++;
			}
		});
	}
	return count;
};

/**
 * Redactions a transaction added: nodes in a step's slice that replaced
 * existing content, net of redactions already inside the replaced range.
 * A node dropped into an empty range is a paste or drag of an existing
 * scribble, and a redaction replaced by a redaction is the same steps applied
 * twice, so neither counts. Only the slice and the replaced range are read.
 */
export const countInsertedRedactions = (tr: Transaction): number => {
	if (!tr.docChanged) {
		return 0;
	}

	let total = 0;
	tr.steps.forEach((step, index) => {
		if (!(step instanceof ReplaceStep || step instanceof ReplaceAroundStep)) {
			return;
		}
		if (step.to <= step.from) {
			return;
		}
		const inserted = countRedactionsInFragment(step.slice.content);
		if (inserted === 0) {
			return;
		}
		const before = tr.docs[index];
		const replaced =
			step instanceof ReplaceAroundStep
				? countRedactionsBetween(before, step.from, step.gapFrom) +
					countRedactionsBetween(before, step.gapTo, step.to)
				: countRedactionsBetween(before, step.from, step.to);
		total += Math.max(0, inserted - replaced);
	});

	return total;
};

// A block-character run counts as one redaction, matching `RedactedContent`.
// Shared safely: `match` with the global flag ignores `lastIndex`.
const TOKEN_PATTERN = new RegExp(REDACTION_MATCHER_SOURCES.join('|'), 'gu');

export const countRedactionTokens = (text: string | null | undefined): number => {
	if (!text) {
		return 0;
	}
	const matches = text.match(TOKEN_PATTERN);
	return matches ? matches.length : 0;
};

export const countNewRedactionTokens = (
	previous: string | null | undefined,
	next: string | null | undefined,
): number => Math.max(0, countRedactionTokens(next) - countRedactionTokens(previous));
